import { useCallback, useEffect, useRef, useState } from 'react'

import { ONE_SECOND_IN_MS } from '~/constants'
import { TChatMessage, TChatTextMessage } from '~/features/chatbot/models'
import {
  isTextChatMessage,
  shouldActivateButton,
} from '~/features/chatbot/utils'
import { TTurnstileTokenAccessor } from '~/features/chatbotV2/hooks/useTurnstileToken'
import {
  CHAT_V2_FORM_SUBMISSION_MESSAGE_TYPE,
  TChatV2MessageNewPayload,
} from '~/features/chatbotV2/models'
import {
  appendChatbotMessageFromServer,
  mapCachedHistoryEntryToChatMessage,
  resolveChatV2ConversationId,
} from '~/features/chatbotV2/utils'
import { EOptionalEnvironmentVariables } from '~/models/config'
import { EMessageSpeaker } from '~/models/features'
import { store } from '~/store'

export type TChatV2Params = {
  agentId: string
  conversationId?: string
  message: string
  type?: string
  source?: string
}

enum EChatV2EventType {
  SessionInit = 'SESSION_INIT',
  NewMessage = 'NEW_MESSAGE',
  // Guest clicking "Start a new conversation" (see the multi-tab persistence
  // design) — formally ends the current conversation and starts a fresh one
  // on the same already-open socket. See restartConversation below.
  SessionRestart = 'SESSION_RESTART',
  SessionReady = 'SESSION_READY',
  // Pushed by the backend when something happens in ANOTHER tab of this same
  // guest (their own message, or the AI's/staff's reply) — never for
  // something THIS tab did itself. See the MessageNew case below.
  MessageNew = 'MESSAGE_NEW',
  // Pushed by the backend when another tab of this same guest just sent a
  // message and the AI is now working on a reply. See the AssistantTyping
  // case below.
  AssistantTyping = 'ASSISTANT_TYPING',
  StreamChunk = 'STREAM_CHUNK',
  StreamEnd = 'STREAM_END',
  // Client-initiated end (guest pressing "End conversation") — distinct from
  // SessionEnded below, which is the server's confirmation. The backend
  // (heyLibbyChat's handleSessionEnd) already accepts this exact event —
  // its own comment documents "an explicit client SESSION_END" as one of
  // three equivalent ways a session ends, alongside the AI's own
  // end_conversation tool call and the inactivity checker. Nothing on the
  // frontend sent it before this.
  SessionEnd = 'SESSION_END',
  // Guest is actively typing — forwarded by heyLibbyChat to any staff
  // watching this conversation in the Inbox (see handlers/typingForward.ts),
  // purely a staff-side indicator. Throttled client-side (see sendTyping
  // below), not sent on every keystroke.
  TypingStart = 'TYPING_START',
  SessionEnded = 'SESSION_ENDED',
  SessionWarning = 'SESSION_WARNING',
  // 2 turns left before the 30-turn conversation limit auto-ends the chat —
  // distinct from SessionWarning above (that one's inactivity-timeout-based
  // and its copy says so; this one needs its own wording since the reason
  // and the remaining-count units are both different).
  TurnLimitWarning = 'TURN_LIMIT_WARNING',
  Error = 'ERROR',
}

// No localhost fallback — matches this codebase's established pattern
// (axiosHttpService/sig4HttpService both throw rather than silently default
// when a base URL is missing) rather than silently pointing every guest's
// browser at their own machine in a real deployed build. Checked lazily
// inside connect() below, not at module scope, so a missing var only breaks
// the chat widget instead of the whole app bundle at import time.
const WS_URL = import.meta.env[
  EOptionalEnvironmentVariables.VITE_CHAT_V2_WS_URL
] as string | undefined
const TOKEN_URL = import.meta.env[
  EOptionalEnvironmentVariables.VITE_CHAT_V2_TOKEN_URL
] as string | undefined
// See EOptionalEnvironmentVariables.VITE_CHAT_V2_LOAD_TEST_BYPASS_SECRET.
// Gated on import.meta.env.DEV (compile-time constant, true only under
// `vite dev`, hardcoded false in any `vite build` output) rather than trusting
// this comment alone — every VITE_ var is statically baked into the bundle in
// EVERY build mode, so an accidental CI/Amplify env var alone wouldn't stop
// it from shipping. The DEV check lets a bundler dead-code-eliminate this
// reference (and the literal secret value) from every real build, so even a
// mistakenly-set var in a shared build environment never reaches shipped JS.
const LOAD_TEST_BYPASS_SECRET = import.meta.env.DEV
  ? (import.meta.env[
      EOptionalEnvironmentVariables.VITE_CHAT_V2_LOAD_TEST_BYPASS_SECRET
    ] as string | undefined)
  : undefined

const MAX_RECONNECT_DELAY_MS = 30 * ONE_SECOND_IN_MS
// How often sendTyping actually sends a TYPING_START while the guest keeps
// typing — a fresh pulse every keystroke would be both wasteful and
// pointless (the Inbox side re-arms its own auto-expiry timeout on each one
// anyway, see useConversationSocket). Must stay comfortably below that
// expiry window (see GUEST_TYPING_EXPIRY_MS there) or the bubble would
// visibly flicker off between pulses while the guest is still typing
// continuously — this value and that one are a matched pair, change them
// together.
const TYPING_THROTTLE_MS = 0.6 * ONE_SECOND_IN_MS
const SESSION_STORAGE_KEY_PREFIX = 'heylibby-chat-v2-session-'

function getStoredSessionId(agentId: string): string {
  try {
    return localStorage.getItem(`${SESSION_STORAGE_KEY_PREFIX}${agentId}`) ?? ''
  } catch {
    // Private browsing / disabled storage — resume just won't work, not fatal.
    return ''
  }
}

function setStoredSessionId(agentId: string, sessionId: string) {
  try {
    localStorage.setItem(`${SESSION_STORAGE_KEY_PREFIX}${agentId}`, sessionId)
  } catch {
    // Ignore — resume is a best-effort convenience, not required for a working chat.
  }
}

function clearStoredSessionId(agentId: string) {
  try {
    localStorage.removeItem(`${SESSION_STORAGE_KEY_PREFIX}${agentId}`)
  } catch {
    // Ignore, see setStoredSessionId.
  }
}

// Separate from SESSION_STORAGE_KEY_PREFIX above and never cleared alongside
// it: guestId identifies the GUEST across every conversation they ever have
// with this agent (getLatestFormSubmission/findMostRecentContactIdByGuestId
// key prefill/"already submitted" lookups off it), not just the one
// conversation sessionId tracks — clearing it on session end would break
// returning-guest prefill the moment their first conversation ends.
//
// Without this, every reconnect (dropped socket, page refresh, reopened tab)
// called POST /token with no guestId in the body, so tokenRequest.ts minted a
// brand-new random one each time (see its own `guestId = body.guestId ||
// 'guest-' + randomUUID()...` fallback). Conversation.guestId is stamped
// ONCE, atomically, from whichever guestId was active at the first real
// message (see heyLibbyChat's sessionPersistService.ts) — so any later
// reconnect's fresh guestId permanently mismatched it, and every guestId-
// gated action after that (form submission, endConversation) started failing
// with 403 "Not authorized" for that guest (Cursor-flagged / Fede-reported).
const GUEST_ID_STORAGE_KEY_PREFIX = 'heylibby-chat-v2-guest-'

function getStoredGuestId(agentId: string): string {
  try {
    return (
      localStorage.getItem(`${GUEST_ID_STORAGE_KEY_PREFIX}${agentId}`) ?? ''
    )
  } catch {
    // Private browsing / disabled storage — falls back to a fresh
    // server-minted guestId every connect, same degraded (but working)
    // behavior as before this fix.
    return ''
  }
}

function setStoredGuestId(agentId: string, guestId: string) {
  try {
    localStorage.setItem(`${GUEST_ID_STORAGE_KEY_PREFIX}${agentId}`, guestId)
  } catch {
    // Ignore — persistence is best-effort, not required for a working chat.
  }
}

// Shared by connect() (minting the token a fresh WS connection opens with)
// and refreshGuestToken (re-minting one for a REST call whose token has
// aged out — see that function's own comment). Returns null on any failure;
// callers decide what that means for them (connect() schedules a
// reconnect, refreshGuestToken just reports no fresh token is available).
// Logs its own failures (with the HTTP status, when there is one) rather
// than leaving that to callers — both callers previously either dropped the
// status or didn't log at all, making a failure in this path undiagnosable
// in production (Cursor-flagged, Medium severity).
async function fetchGuestToken({
  agentId,
  turnstile,
}: {
  agentId: string
  turnstile: TTurnstileTokenAccessor
}): Promise<{ token: string; guestId: string } | null> {
  if (!TOKEN_URL) return null

  const storedGuestId = getStoredGuestId(agentId)
  // Single-use server-side (see tokenRequest.ts) — rotate immediately
  // regardless of whether this fetch ultimately succeeds, so a retry never
  // resends the same already-spent token.
  //
  // getFreshToken (not getToken) — a Turnstile-required gateway rejects a
  // /token POST with no turnstileToken outright, and the guest JWT's own
  // 5-minute lifetime tends to go stale around the same time as the cached
  // Turnstile solve (MAX_TOKEN_AGE_MS, 4 minutes), so a refresh triggered by
  // the JWT expiring could otherwise race straight into that rejection —
  // breaking self-heal in exactly the scenario it exists for (Cursor-flagged,
  // High severity). getFreshToken waits briefly for a solve in progress
  // instead of handing back '' immediately.
  const turnstileToken = await turnstile.getFreshToken()
  if (turnstileToken) turnstile.consumeToken()

  const tokenRes = await fetch(`${TOKEN_URL}/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      agentId,
      ...(storedGuestId && { guestId: storedGuestId }),
      ...(turnstileToken && { turnstileToken }),
      ...(LOAD_TEST_BYPASS_SECRET && {
        loadTestBypassSecret: LOAD_TEST_BYPASS_SECRET,
      }),
    }),
  })
  if (!tokenRes.ok) {
    // eslint-disable-next-line no-console
    console.error('[ChatV2] /token request failed', {
      status: tokenRes.status,
      hadTurnstileToken: !!turnstileToken,
    })
    return null
  }

  const { token, guestId: resolvedGuestId } = (await tokenRes.json()) as {
    token: string
    guestId: string
  }
  // Persist whatever the server resolved — the guestId we sent back if we
  // had one, or the fresh one it minted on a genuinely first visit. Every
  // later connect/reconnect/refresh for this agent then resends the same
  // identity instead of getting a new random one each time.
  if (resolvedGuestId) setStoredGuestId(agentId, resolvedGuestId)
  return { token, guestId: resolvedGuestId }
}

// Maps SESSION_READY history — form_submission rows use structured details only.
function mapHistoryToMessages(history: unknown): TChatMessage[] {
  if (!Array.isArray(history)) return []

  return history
    .map((item) => {
      if (typeof item !== 'object' || item === null) {
        return null
      }

      const entry = item as {
        role?: string
        content?: string
        type?: string
        details?: string
      }

      if (
        typeof entry.content !== 'string' &&
        entry.type !== CHAT_V2_FORM_SUBMISSION_MESSAGE_TYPE
      ) {
        return null
      }

      return mapCachedHistoryEntryToChatMessage({
        role: entry.role,
        content: entry.content,
        type: entry.type,
        details: entry.details,
      })
    })
    .filter((message): message is TChatMessage => message !== null)
}

// Recomputes showBookAMeeting for a REPLACED message list (a resume or a
// restart), mirroring finalizeAssistantMessage's own turn-by-turn OR-forever
// logic below — walks the assistant messages in order, accumulating exactly
// as they would have if each had streamed in live, so a genuine resume
// still shows the CTA if it was ever earned mid-conversation, while a
// restart's fresh (or greeting-only) history correctly comes back false
// (Cursor-flagged, Medium severity: SESSION_READY previously left
// showBookAMeeting untouched, so a stale CTA from the ended conversation
// carried straight into the new one).
function computeShowBookAMeeting(messages: TChatMessage[]): boolean {
  const assistantMessages = messages.filter(
    (m): m is TChatTextMessage =>
      m.speaker === EMessageSpeaker.assistant && isTextChatMessage(m),
  )
  return assistantMessages.some((m, index) =>
    shouldActivateButton(m.message, assistantMessages.slice(0, index + 1)),
  )
}

// v2 (WebSocket) chat client — used by the production guest widget
// (features/chatbotV2, gated per-agent behind ?chatV2=true + admin, see
// pages/chatbot) as well as the admin-only /chat/v2 clone page. Matches
// heyLibbyChat's packages/shared/src/events.ts protocol (EEventType) — kept
// as a local duplicate here since the frontend can't import a backend
// workspace package.
//
// `store.getState()`/`store.getState().setChatbot(...)` are used throughout
// instead of the reactive `store()` hook values, since the WebSocket event
// handlers are set up once (inside the effect) and must always act on the
// live current state, not a stale render-time snapshot.
const useChatV2 = (turnstile: TTurnstileTokenAccessor) => {
  const agentId = store((state) => state.chatbot.agent.id)
  const [isPending, setIsPending] = useState(false)
  // Real React state (unlike the closure-local `sessionEnded` below) so
  // DialogInput can actually disable the input/send button once the session
  // ends — matches v1's hasConversationEnded (see dialogInput/index.tsx).
  // Mirrored into a ref (same reasoning as isOtherTabAwaitingReplyRef below)
  // so restartConversation's WS-handler-set-up-once closure can read the
  // CURRENT value — it deliberately allows a restart in this ended state,
  // unlike every other guarded action.
  const [hasSessionEnded, setHasSessionEndedState] = useState(false)
  const [guestToken, setGuestToken] = useState<string | null>(null)
  const [isSessionReady, setIsSessionReady] = useState(false)
  const hasSessionEndedRef = useRef(false)
  const setHasSessionEnded = useCallback((value: boolean) => {
    hasSessionEndedRef.current = value
    setHasSessionEndedState(value)
  }, [])
  // Lock covering the window between EITHER restartConversation() branch
  // (an ended-session reconnect, or a live-socket SESSION_RESTART send) and
  // the fresh session's own SESSION_READY — only ever consumed by isAiBusy
  // (button-disable) and restartConversation's own re-entry guard below,
  // deliberately NOT by Dialog, unlike isPending (see the live-socket
  // branch's own comment for why that distinction matters).
  //
  // Originally just the ended-session case: forceReconnect just re-runs the
  // connection effect; it doesn't touch hasSessionEnded or sessionReadyRef,
  // and neither the button (isAiBusy) nor restartConversation's own guard
  // otherwise knew a reconnect was already in flight (Cursor-flagged, Medium
  // severity). A second click in that window used to call forceReconnect
  // again, aborting the in-flight connect and restarting it from scratch —
  // the guest saw no change after the first click and effectively cancelled
  // their own new conversation by clicking again.
  const [isReconnectingAfterEnd, setIsReconnectingAfterEndState] =
    useState(false)
  const isReconnectingAfterEndRef = useRef(false)
  const setIsReconnectingAfterEnd = useCallback((value: boolean) => {
    isReconnectingAfterEndRef.current = value
    setIsReconnectingAfterEndState(value)
  }, [])
  const wsRef = useRef<WebSocket | null>(null)
  // True only once the backend's SESSION_READY reply has actually arrived —
  // the raw WebSocket becomes OPEN as soon as the handshake completes, well
  // before the SESSION_INIT -> session-creation -> SESSION_READY round trip
  // finishes. Gating mutate() on ws.readyState alone lets a message sent
  // right after connecting race ahead of that round trip and get rejected
  // server-side with "Session not initialised".
  const sessionReadyRef = useRef(false)
  const assistantIndexRef = useRef<number | null>(null)
  // Tracks whether THIS tab has ever received a SESSION_READY before — see
  // the SessionReady case below for why this matters (the history-replay
  // bug fix).
  const hasEverReceivedSessionReadyRef = useRef(false)
  // Mirrors `isPending` for use inside the WS event-handler closures below,
  // which are set up once per connection and must read the CURRENT value,
  // not a stale one captured at mount (same reasoning as sessionReadyRef —
  // see this file's own top-of-file comment).
  const isPendingRef = useRef(false)
  // True when ANOTHER tab of this same guest is currently waiting on an AI
  // reply (this tab received ASSISTANT_TYPING for a message it didn't
  // send) — gates the "Start a new conversation" button so it can't race an
  // in-flight turn on another tab (see the multi-tab persistence design's
  // Error Handling section). Kept in a ref alongside the React state below
  // for the same stale-closure reason as isPendingRef.
  const [isOtherTabAwaitingReply, setIsOtherTabAwaitingReplyState] =
    useState(false)
  const isOtherTabAwaitingReplyRef = useRef(false)
  const setIsOtherTabAwaitingReply = useCallback((value: boolean) => {
    isOtherTabAwaitingReplyRef.current = value
    setIsOtherTabAwaitingReplyState(value)
  }, [])
  // Throttle window for sendTyping below — resending on every keystroke
  // would flood the socket and, downstream, every staff watcher's inbox.
  const lastTypingSentAtRef = useRef(0)
  // Bumped by forceReconnect (see restartConversation below) to deliberately
  // re-run the connection effect on demand — its own onclose handler blocks
  // automatic reconnection once a session has ended (sessionEnded closure
  // flag, see that effect's own comment), by design: a transient drop must
  // never silently auto-start a new conversation. This nonce is the escape
  // hatch for the one case that SHOULD reconnect — an explicit "Start a new
  // conversation" click after the old one already ended — without weakening
  // that guard for every other (unwanted) case.
  const [reconnectNonce, setReconnectNonce] = useState(0)
  const forceReconnect = useCallback(() => {
    setReconnectNonce((n) => n + 1)
  }, [])

  useEffect(() => {
    // turnstile.isReady is true immediately when VITE_TURNSTILE_SITE_KEY is
    // unset (nothing to wait for — see useTurnstileToken) and flips true once
    // when it IS set, after the widget's first solve. Re-running this effect
    // on that single false->true transition is intentional: it lets the very
    // first connect() wait for a real token instead of firing before one
    // exists (the widget's own onError/console.error is the only surfaced
    // signal if it never solves — see useTurnstileToken).
    if (!agentId || !turnstile.isReady) return () => {}

    let cancelled = false
    let reconnectTimeout: ReturnType<typeof setTimeout> | undefined
    let reconnectDelay = ONE_SECOND_IN_MS
    // Set once the server ends the session on its own (10-min inactivity
    // auto-end). A SESSION_ENDED is always followed by the socket closing —
    // without this flag, onclose would treat that close like any transient
    // drop and silently open a brand-new backend session while the ended
    // conversation's messages stay on screen. Reset on a fresh SESSION_READY
    // so a later, actually-new connection can reconnect again if it drops.
    let sessionEnded = false
    // Set on the first ERROR event of this session and never reset
    // (Cursor-flagged, Medium severity, refined after manual testing against
    // a real dropped backend connection): an ERROR always closes the socket
    // into the same reconnect loop, so a persistently-failing SESSION_INIT
    // (e.g. a sustained backend issue) previously appended a fresh
    // "something went wrong" bubble on every single retry — stacking up
    // duplicates every few seconds instead of giving one heads-up while the
    // widget keeps quietly retrying. Deliberately once per session, not once
    // per outage episode — a later, separate hiccup after a successful
    // reconnect stays silent (background retry only) rather than showing
    // the notice again, so one flaky stretch doesn't read as a repeatedly
    // unreliable chat.
    let errorNoticeShown = false

    const scheduleReconnect = () => {
      if (cancelled || sessionEnded) return
      reconnectTimeout = setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        if (!cancelled) connect()
      }, reconnectDelay)
      reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY_MS)
    }

    const appendAssistantChunk = (chunk: string) => {
      const { chatbot, setChatbot } = store.getState()
      const messages = [...chatbot.messages]
      if (assistantIndexRef.current === null) {
        assistantIndexRef.current = messages.length
        messages.push({ speaker: EMessageSpeaker.assistant, message: chunk })
      } else {
        const idx = assistantIndexRef.current
        const current = messages[idx]
        const priorText = isTextChatMessage(current) ? current.message : ''
        messages[idx] = {
          speaker: EMessageSpeaker.assistant,
          message: priorText + chunk,
        }
      }
      setChatbot({ messages })
    }

    const finalizeAssistantMessage = (content: string) => {
      const { chatbot, setChatbot } = store.getState()
      const messages = [...chatbot.messages]
      if (assistantIndexRef.current !== null) {
        messages[assistantIndexRef.current] = {
          speaker: EMessageSpeaker.assistant,
          message: content,
        }
      } else {
        messages.push({ speaker: EMessageSpeaker.assistant, message: content })
      }

      // Mirrors v1's useChat onSuccess — once the Calendly CTA has been
      // shown it should never be hidden again, so only ever OR it in here.
      const assistantMessages = messages.filter(
        (chatMessage): chatMessage is TChatTextMessage =>
          chatMessage.speaker === EMessageSpeaker.assistant &&
          isTextChatMessage(chatMessage),
      )
      const shouldShowBookAMeeting = shouldActivateButton(
        content,
        assistantMessages,
      )

      setChatbot({
        messages,
        showBookAMeeting: chatbot.showBookAMeeting || shouldShowBookAMeeting,
      })
      assistantIndexRef.current = null
    }

    // Called on any terminal/error condition (server ERROR, socket error,
    // socket close) so a dropped connection or failed stream can never leave
    // isPending stuck true (send button disabled forever) or a stale
    // assistantIndexRef pointing at a message a future chunk shouldn't
    // append to.
    const resetStreamState = () => {
      assistantIndexRef.current = null
      setIsPending(false)
      isPendingRef.current = false
    }

    const connect = () => {
      if (!WS_URL || !TOKEN_URL) {
        console.error(
          '[ChatV2] VITE_CHAT_V2_WS_URL / VITE_CHAT_V2_TOKEN_URL are not set — cannot connect. Add them to .env (see .env.example).',
        )
        return
      }

      ;(async () => {
        // Read fresh on every (re)connect attempt — a SESSION_ENDED between
        // attempts clears this, so a later retry correctly starts a new
        // session instead of trying to resume one the server already ended.
        const storedSessionId = getStoredSessionId(agentId)

        const tokenResult = await fetchGuestToken({ agentId, turnstile })
        if (!tokenResult) {
          console.error('[ChatV2] Failed to fetch guest token')
          scheduleReconnect()
          return
        }
        if (cancelled) return
        const { token } = tokenResult
        setGuestToken(token)

        // Token travels as the WS subprotocol (Sec-WebSocket-Protocol), not a
        // ?token= query param — ALB access logging persists the full request
        // line, including the query string, to S3 for the retention window,
        // but does not capture this header. guestAuth.ts reads it the same way.
        const ws = new WebSocket(`${WS_URL}/guest/v2preview`, [token])
        wsRef.current = ws
        sessionReadyRef.current = false
        setIsSessionReady(false)

        ws.onopen = () => {
          // Read fresh at send time (store.getState(), not a reactive value
          // captured at mount) — matches heyLibbyChat's sessionInit.ts, which
          // expects this on every SESSION_INIT (fresh or resume) so it
          // reflects wherever the guest currently is, not just where they
          // were when this hook first mounted. Populated by
          // useEmbeddedChatContext from the parent embed script's
          // postMessage — omitted entirely if not set yet (e.g. this
          // connection attempt races ahead of that message), which is fine:
          // a later reconnect's SESSION_INIT will pick up whatever value is
          // current by then.
          const { currentPage } = store.getState().chatbot
          ws.send(
            JSON.stringify({
              id: crypto.randomUUID(),
              type: EChatV2EventType.SessionInit,
              payload: {
                ...(storedSessionId ? { sessionId: storedSessionId } : {}),
                ...(currentPage ? { context: { currentPage } } : {}),
              },
            }),
          )
        }

        ws.onmessage = (rawEvent) => {
          let event: { type: string; payload: Record<string, unknown> }
          try {
            event = JSON.parse(rawEvent.data as string)
          } catch {
            return
          }

          switch (event.type) {
            case EChatV2EventType.SessionReady: {
              // Connection confirmed — reset backoff so a later drop starts
              // reconnecting quickly again instead of at the last delay.
              reconnectDelay = ONE_SECOND_IN_MS
              sessionEnded = false
              // errorNoticeShown is deliberately NOT reset here — once per
              // session (this hook's whole mount, not per outage episode) is
              // the intended guest-facing UX: a later, separate hiccup after
              // a successful reconnect stays silent (background retry only)
              // rather than showing the notice again.
              setHasSessionEnded(false)
              // Releases the ended-session restart lock (see
              // restartConversation) — whether this SESSION_READY is the
              // one forceReconnect was waiting on, or an ordinary resume/
              // fresh connect that never needed the lock at all, either way
              // there's no reconnect left in flight once this fires.
              setIsReconnectingAfterEnd(false)
              sessionReadyRef.current = true
              setIsSessionReady(true)
              if (typeof event.payload.sessionId === 'string') {
                setStoredSessionId(agentId, event.payload.sessionId)
              }

              const conversationId = resolveChatV2ConversationId(event.payload)
              if (conversationId) {
                store.getState().setChatbot({ conversationId })
              }
              // A fresh or resumed session is, by definition, never
              // mid-sibling-turn — unconditionally clear this so a tab that
              // reconnects (e.g. a socket blip) while another tab's
              // ASSISTANT_TYPING was in flight doesn't get stuck forever
              // with its "Start a new conversation" button disabled (that
              // reconnect deregisters this tab from the guest-watcher set,
              // so it will never receive the MESSAGE_NEW that would
              // otherwise have cleared this).
              setIsOtherTabAwaitingReply(false)
              // A fresh SESSION_READY means whatever THIS tab was itself
              // mid-stream on is gone — the transcript below is about to be
              // replaced wholesale (or the tab just resumed cleanly). Without
              // this, a tab that was mid-reply when another tab restarted (or
              // that reconnected then restarted) kept a stale
              // assistantIndexRef pointing past the end of the new, shorter
              // message list — the next StreamEnd/StreamChunk for a NEW turn
              // would then write into/spread that stale index, corrupting the
              // rendered list — and a stuck isPending with no other trigger
              // left to ever reset it, permanently disabling the send button
              // on the new conversation (Cursor-flagged, Medium severity).
              resetStreamState()

              // Replace the visible history with whatever the backend just
              // sent back — but only once this tab has already received a
              // SESSION_READY before, or the backend actually has real
              // history to show. A brand-new visitor's very first
              // SESSION_READY has empty history and must NOT wipe a
              // locally-rendered chatIntro greeting. This was the real bug
              // behind "refreshing the page restarts the conversation": the
              // backend already sent history back correctly on every
              // resume, this handler just never read it. Every other case —
              // a genuine resume with real history, a later reconnect after
              // a network blip, or a restart's deliberately-empty history
              // (see restartConversation below) — replaces it, since the
              // backend's history is always the authoritative full
              // transcript for whatever conversation this tab is now on.
              const isFirstResolution = !hasEverReceivedSessionReadyRef.current
              hasEverReceivedSessionReadyRef.current = true
              const { history } = event.payload
              if (
                Array.isArray(history) &&
                (history.length > 0 || !isFirstResolution)
              ) {
                const mappedMessages = mapHistoryToMessages(history)
                if (mappedMessages.length > 0) {
                  store.getState().setChatbot({
                    messages: mappedMessages,
                    showBookAMeeting: computeShowBookAMeeting(mappedMessages),
                  })
                } else {
                  // Empty history (e.g. right after restartConversation)
                  // must still show the agent's greeting, same as a
                  // genuinely fresh page load — otherwise the guest sees a
                  // jarringly blank chat window instead of the chatIntro
                  // usePopulateChatbot seeds on initial load (see
                  // getAgentAndChatbotDetails in
                  // features/chatbot/hooks/usePopulateChatbot/utils.ts).
                  const { agent } = store.getState().chatbot
                  const greetingMessages = [
                    {
                      speaker: EMessageSpeaker.assistant,
                      message: agent.chatIntro ?? '',
                    },
                  ]
                  store.getState().setChatbot({
                    messages: greetingMessages,
                    showBookAMeeting: computeShowBookAMeeting(greetingMessages),
                  })
                }
              }
              break
            }

            case EChatV2EventType.StreamChunk:
              appendAssistantChunk((event.payload.content as string) ?? '')
              break

            case EChatV2EventType.StreamEnd:
              finalizeAssistantMessage((event.payload.content as string) ?? '')
              setIsPending(false)
              isPendingRef.current = false
              break

            case EChatV2EventType.MessageNew: {
              // Pushed by the backend when something happens in ANOTHER tab
              // of this same guest — their own message, or the AI's/staff's
              // reply (see the multi-tab persistence design) — never for
              // something THIS tab did itself: the backend always excludes
              // the originating tab's own socket from this broadcast (see
              // heyLibbyChat's handlers/newMessage.ts). No dedup needed here
              // for that reason.
              setIsOtherTabAwaitingReply(false)
              const payload = event.payload as TChatV2MessageNewPayload
              const chatMessage = mapCachedHistoryEntryToChatMessage({
                role: payload.role,
                content: payload.content,
                type: payload.type,
                details: payload.details,
              })
              if (!chatMessage) break
              appendChatbotMessageFromServer(chatMessage)
              const { chatbot, setChatbot } = store.getState()
              const { speaker } = chatMessage
              const content = isTextChatMessage(chatMessage)
                ? chatMessage.message
                : ''
              const { messages } = chatbot
              // Mirrors finalizeAssistantMessage's own OR-forever logic below —
              // this was the only one of the three places that touch messages
              // (this one, finalizeAssistantMessage, and SessionReady's
              // computeShowBookAMeeting) that never recomputed the CTA
              // (Cursor-flagged, Medium severity): a guest with a second tab
              // open would see the booking line arrive here via MESSAGE_NEW,
              // but never get the Book a meeting button on that tab until it
              // reloaded or a later message ran through one of the other two
              // paths.
              const shouldShowBookAMeeting =
                speaker === EMessageSpeaker.assistant &&
                shouldActivateButton(
                  content,
                  messages.filter(
                    (m): m is TChatTextMessage =>
                      m.speaker === EMessageSpeaker.assistant &&
                      isTextChatMessage(m),
                  ),
                )
              setChatbot({
                messages,
                showBookAMeeting:
                  chatbot.showBookAMeeting || shouldShowBookAMeeting,
              })
              break
            }

            case EChatV2EventType.AssistantTyping:
              // Another tab of this same guest just sent a message and the
              // AI is now working on a reply (see the multi-tab persistence
              // design) — gates the "Start a new conversation" button so it
              // can't race an in-flight turn happening on another tab.
              setIsOtherTabAwaitingReply(true)
              break

            case EChatV2EventType.SessionEnded: {
              console.log('[ChatV2] Session ended')
              sessionEnded = true
              // The socket itself may still report OPEN for a moment after
              // this — sessionReadyRef is the actual guard mutate() checks,
              // and it was never reset here, so a message sent right after
              // SESSION_ENDED (but before the server closes the connection)
              // still went out and came back rejected with "Session expired"
              // instead of failing fast client-side.
              sessionReadyRef.current = false
              setIsSessionReady(false)
              setHasSessionEnded(true)
              clearStoredSessionId(agentId)
              resetStreamState()
              // Releases restartConversation's busy lock too (Cursor-flagged,
              // Medium severity) — nothing else in this lifecycle ever will:
              // scheduleReconnect no-ops once sessionEnded is true (see
              // onclose below), so if either restart path (this live-socket
              // one, or the ended-session reconnect branch) set this and then
              // got a SECOND SESSION_ENDED instead of the SESSION_READY it
              // was waiting for, the lock stayed true forever with no future
              // trigger left to clear it — permanently disabling "Start a new
              // conversation" until a page refresh.
              setIsReconnectingAfterEnd(false)
              // Visible in-chat notice, not just a console log — the guest
              // otherwise has no indication further messages won't go
              // anywhere until they refresh (see mutate()'s sessionReadyRef
              // check, which silently no-ops once this fires).
              const { chatbot, setChatbot } = store.getState()
              setChatbot({
                messages: [
                  ...chatbot.messages,
                  {
                    speaker: EMessageSpeaker.assistant,
                    message:
                      'This conversation has ended. Refresh the page to start a new one.',
                  },
                ],
              })
              break
            }

            case EChatV2EventType.SessionWarning: {
              // Visible in-chat notice, same reasoning as SessionEnded below —
              // v1 showed a client-driven inactivity warning; v2 relies on
              // this server event to replace it (see dialogInput's own
              // comment), so a console.log alone leaves guests with no
              // heads-up before the session auto-ends.
              const secondsRemaining = Number(event.payload.secondsRemaining)
              const minutesRemaining = Math.max(
                1,
                Math.round(secondsRemaining / 60),
              )
              const { chatbot, setChatbot } = store.getState()
              setChatbot({
                messages: [
                  ...chatbot.messages,
                  {
                    speaker: EMessageSpeaker.assistant,
                    message: `This conversation will end in about ${minutesRemaining} minute${minutesRemaining === 1 ? '' : 's'} due to inactivity. Send a message to keep it going.`,
                  },
                ],
              })
              break
            }

            case EChatV2EventType.TurnLimitWarning: {
              // Visible in-chat notice, same reasoning as SessionWarning
              // above — a heads-up before the 30-turn limit auto-ends the
              // chat, not a replacement for the real reply that arrived via
              // StreamEnd just before this event.
              const turnsRemaining = Number(event.payload.turnsRemaining)
              const { chatbot, setChatbot } = store.getState()
              setChatbot({
                messages: [
                  ...chatbot.messages,
                  {
                    speaker: EMessageSpeaker.assistant,
                    message: `This conversation is nearing its limit — ${turnsRemaining} more message${turnsRemaining === 1 ? '' : 's'} before it ends automatically.`,
                  },
                ],
              })
              break
            }

            case EChatV2EventType.Error: {
              console.error('[ChatV2] Server error:', event.payload)

              // Distinguish "MY connection failed" from "a sibling tab's
              // turn failed" by payload shape, not isPendingRef
              // (Cursor-flagged, High severity: isPendingRef is only ever
              // true during a NEW_MESSAGE send, so a SESSION_INIT or
              // SESSION_RESTART failure — this tab's OWN connection
              // breaking, before it ever sent a message — was wrongly
              // routed into the passive branch below: no notice, no
              // ws.close(), no reconnect, leaving the widget silently dead
              // with sessionReadyRef stuck false forever). heyLibbyChat's
              // consumer.ts publishes exactly two shapes: this tab's own
              // failure always includes `message` (the direct,
              // job.gatewayId/socketId-targeted publish), while a fanned-
              // out notice about a SIBLING tab's failed turn never does
              // (see consumer.ts's guest-watcher loop) — so payload shape
              // reliably tells them apart where isPendingRef could not.
              if (typeof event.payload.message !== 'string') {
                // A sibling tab's turn failed, not this connection's own.
                // This tab's connection is healthy; only clear the
                // "another tab is waiting on a reply" flag — don't show an
                // error notice or reconnect.
                setIsOtherTabAwaitingReply(false)
                break
              }

              // A tab can be mid-send (isPendingRef true) AND have
              // isOtherTabAwaitingReply true at the same time — a local
              // send and a sibling tab's in-flight turn aren't mutually
              // exclusive. Clear it here too so this tab's own failed turn
              // can't leave it stuck.
              setIsOtherTabAwaitingReply(false)
              resetStreamState()
              // Visible in-chat notice, same reasoning as SessionEnded/
              // TurnLimitWarning above — previously this was a silent
              // console.error with no guest-facing message at all: the
              // guest's message would appear to go nowhere, with no
              // explanation, while the socket quietly closed and
              // reconnected in the background.
              //
              // Only the FIRST error of this whole session shows a notice —
              // every ERROR below closes into the same reconnect loop, so
              // without this guard a persistently-failing SESSION_INIT
              // stacked up a fresh, identical bubble on every retry instead
              // of giving one heads-up while the widget keeps retrying
              // quietly in the background.
              if (!errorNoticeShown) {
                errorNoticeShown = true
                const { chatbot, setChatbot } = store.getState()
                setChatbot({
                  messages: [
                    ...chatbot.messages,
                    {
                      speaker: EMessageSpeaker.assistant,
                      message:
                        'Something went wrong on our end — please try again in a moment.',
                    },
                  ],
                })
              }
              // A server-side error (e.g. during SESSION_INIT processing)
              // otherwise leaves the socket open but never ready, with no
              // path back to a working state short of a manual page reload
              // — sessionReadyRef only ever becomes true via SESSION_READY,
              // which this connection will now never receive. Closing
              // triggers the existing onclose -> scheduleReconnect flow,
              // which retries SESSION_INIT (resuming via the stored
              // sessionId if one was ever actually established).
              ws.close()
              break
            }

            default:
              break
          }
        }

        ws.onerror = (err) => {
          console.error('[ChatV2] WebSocket error:', err)
          resetStreamState()
        }

        ws.onclose = () => {
          if (wsRef.current === ws) wsRef.current = null
          sessionReadyRef.current = false
          resetStreamState()
          scheduleReconnect()
        }
      })().catch((err: unknown) => {
        console.error('[ChatV2] Connection setup failed:', err)
        scheduleReconnect()
      })
    }

    connect()

    return () => {
      cancelled = true
      if (reconnectTimeout) clearTimeout(reconnectTimeout)
      wsRef.current?.close()
      wsRef.current = null
    }
    // turnstile.getToken/consumeToken are stable (useCallback, empty deps in
    // useTurnstileToken) — only turnstile.isReady's transition matters here.
    // reconnectNonce is intentionally in this array purely to re-trigger this
    // effect on demand (see forceReconnect above) — its VALUE is never read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, turnstile.isReady, reconnectNonce])

  // Returns whether the message was actually sent — callers must only clear
  // their input on `true`. Otherwise (e.g. the socket isn't OPEN yet during
  // the token-fetch/handshake window, or a reconnect is in flight) the typed
  // text would vanish from both the chat and the input with no way to retry
  // short of retyping it.
  const mutate = useCallback((params: TChatV2Params): boolean => {
    // Check the socket is actually open BEFORE adding the optimistic user
    // message — otherwise a message typed while the connection is still
    // establishing (a fetch + WS handshake) appears sent and the input
    // clears, but nothing was ever delivered and there's no retry path.
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN || !sessionReadyRef.current) {
      console.error('[ChatV2] Cannot send — session not ready yet')
      return false
    }

    const { chatbot, setChatbot } = store.getState()
    setChatbot({
      messages: chatbot.messages.concat({
        speaker: EMessageSpeaker.user,
        message: params.message,
      }),
    })

    setIsPending(true)
    isPendingRef.current = true
    ws.send(
      JSON.stringify({
        id: crypto.randomUUID(),
        type: EChatV2EventType.NewMessage,
        payload: {
          content: params.message,
          // dialogInput already builds these from the URL (?istesting=true
          // -> type: 'test', useChatSource -> source) on every send — this
          // used to silently drop both, so heyLibbyChat's chat-service never
          // saw test mode at all (no ?istesting=true parity with v1's
          // isTest, which gates systemContext/vectorData capture — see
          // handlers/newMessage.ts).
          ...(params.type && { type: params.type }),
          ...(params.source && { source: params.source }),
        },
      }),
    )
    return true
  }, [])

  // Guest pressing "End conversation" in the close-confirmation dialog. Fires the
  // server's full end-of-conversation flow (contact/analysis extraction,
  // owner notification) immediately rather than waiting for the 10-minute
  // inactivity timeout — same backend flow, just requested explicitly
  // instead of inferred from silence. No optimistic local state change:
  // the server's own SessionEnded reply (already handled above) is what
  // sets hasSessionEnded and renders the "conversation has ended" message,
  // so a dropped connection here can't leave the UI claiming an end that
  // never actually happened server-side.
  const endConversation = useCallback((): boolean => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN || !sessionReadyRef.current) {
      console.error('[ChatV2] Cannot end conversation — session not ready yet')
      return false
    }

    ws.send(
      JSON.stringify({
        id: crypto.randomUUID(),
        type: EChatV2EventType.SessionEnd,
        payload: {},
      }),
    )
    return true
  }, [])

  // Guest clicking "Start a new conversation" (see the multi-tab
  // persistence design) — formally ends the current conversation (same
  // server-side analysis/notification flow as endConversation above) and
  // starts a fresh one on this SAME already-open socket — no reconnect
  // needed. No local state reset here: the server's own SESSION_READY
  // reply (already handled above) is what actually clears chatbot.messages
  // and stores the new session id, and every currently-open tab of this
  // guest receives that same broadcast and switches together, not just
  // this one.
  //
  // Deliberately allowed when hasSessionEndedRef is true, not just when
  // sessionReadyRef is — unlike mutate()/sendTyping()/endConversation()
  // above, which must stay blocked once the session has ended.
  //
  // (Cursor-flagged, High severity: an earlier version of this comment
  // claimed a guest whose conversation just ended "still has an open,
  // authenticated socket" — false. SESSION_ENDED is always followed by the
  // backend closing the socket, and this hook's onclose handler
  // deliberately blocks automatic reconnection afterward (see the
  // `sessionEnded` closure flag in the connection effect above) — a
  // transient drop must never silently auto-start a new conversation. So by
  // the time a guest reacts and clicks, wsRef.current is reliably null, and
  // sending SESSION_RESTART on a dead socket was a silent no-op.)
  //
  // The actual fix: once the session has already ended, there's nothing
  // left to "restart" server-side in the SESSION_RESTART sense — it's
  // already ended and already deleted from Redis. What's needed is just a
  // fresh connection, the same thing a page refresh already achieves today.
  // forceReconnect (an explicit, click-triggered escape hatch — never
  // automatic) re-runs the connection effect, which opens a new socket and
  // sends a sessionId-less SESSION_INIT (the old id was already cleared on
  // SESSION_ENDED), landing on a genuinely new session exactly like a
  // normal fresh connect.
  const restartConversation = useCallback((): boolean => {
    if (
      isPendingRef.current ||
      isOtherTabAwaitingReplyRef.current ||
      isReconnectingAfterEndRef.current
    ) {
      console.error(
        '[ChatV2] Cannot start a new conversation — a reply is still in flight, or a reconnect is already underway',
      )
      return false
    }

    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN && sessionReadyRef.current) {
      ws.send(
        JSON.stringify({
          id: crypto.randomUUID(),
          type: EChatV2EventType.SessionRestart,
          payload: {},
        }),
      )
      // Mirrors SESSION_ENDED's own immediate sessionReadyRef reset above —
      // the backend starts tearing down the current conversation as soon as
      // it receives this, so treat this tab as not-ready client-side right
      // away instead of waiting for the round trip (Cursor-flagged, Medium
      // severity: without this, sessionReadyRef stayed true and isPending
      // stayed false until the fresh session's SESSION_READY arrived, so the
      // old, being-torn-down thread stayed on screen and still accepted
      // input/sends in that window).
      sessionReadyRef.current = false
      setIsSessionReady(false)
      // isReconnectingAfterEnd, NOT isPending, for the UI-busy side of this
      // lock — Dialog renders its AI-typing bubble directly off isPending
      // (`{isPending && <LoadingBubbles />}`), which is exactly right for a
      // real in-flight send but wrong here: setting isPending true after a
      // restart popped that bubble under the OLD, about-to-be-replaced
      // thread, making the restart look like the AI was still answering
      // right as the guest asked to start fresh (Cursor-flagged, Medium
      // severity, found reviewing the earlier isPending-based fix).
      // isReconnectingAfterEnd only ever reaches isAiBusy (a button-disable),
      // never Dialog, and — now used by both restartConversation branches,
      // see its own declaration comment above — is already unconditionally
      // reset by SESSION_READY below regardless of which branch set it, or,
      // if this round trip itself drops, self-heals the same way the
      // ended-session branch already did before this fix existed (see that
      // branch's own comment).
      setIsReconnectingAfterEnd(true)
      return true
    }

    if (hasSessionEndedRef.current) {
      setIsReconnectingAfterEnd(true)
      forceReconnect()
      return true
    }

    console.error(
      '[ChatV2] Cannot start a new conversation — session not ready yet',
    )
    return false
  }, [forceReconnect, setIsReconnectingAfterEnd])

  // Called from DialogInput on every input change — throttled here (not by
  // the caller) so any call site can fire it freely on every keystroke.
  // Silently no-ops when the socket isn't ready yet, same as mutate() above,
  // but returns nothing: missing one typing pulse during the handshake
  // window isn't worth a retry path the way a lost message would be.
  const sendTyping = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN || !sessionReadyRef.current) {
      return
    }

    const now = Date.now()
    if (now - lastTypingSentAtRef.current < TYPING_THROTTLE_MS) return
    lastTypingSentAtRef.current = now

    ws.send(
      JSON.stringify({
        id: crypto.randomUUID(),
        type: EChatV2EventType.TypingStart,
        payload: {},
      }),
    )
  }, [])

  // Mints a fresh guest token WITHOUT touching the WebSocket — for a REST
  // call (see guestChatGet/guestChatPost's own `refreshToken` param) whose
  // token has aged out. Guest JWTs are deliberately short-lived (5 minutes —
  // tokenRequest.ts's expiresIn) and only get re-minted as a side effect of
  // the socket itself reconnecting; restartConversation reuses an
  // already-open socket when it can (see its own comment), so a long-running
  // conversation's REST calls can keep using a token well past that window
  // with nothing to naturally refresh it. Updates the shared guestToken
  // state too (not just returning the new value) so every OTHER caller
  // benefits immediately instead of each hitting its own 401-then-refresh
  // cycle one at a time.
  const refreshGuestToken = useCallback(async (): Promise<string | null> => {
    if (!agentId) return null
    const result = await fetchGuestToken({ agentId, turnstile })
    if (!result) {
      // fetchGuestToken already logged the HTTP status (or lack of
      // TOKEN_URL) — this one is just the self-heal-specific context, so an
      // operator can tell "a REST call's 401 recovery failed" apart from
      // "the initial connect's token mint failed" in the logs.
      // eslint-disable-next-line no-console
      console.error(
        '[ChatV2] refreshGuestToken: could not mint a replacement guest token',
      )
      return null
    }
    setGuestToken(result.token)
    return result.token
  }, [agentId, turnstile])

  return {
    mutate,
    isPending,
    hasSessionEnded,
    endConversation,
    restartConversation,
    guestToken,
    refreshGuestToken,
    isSessionReady,
    // isReconnectingAfterEnd folded in here (not a separate returned flag)
    // so the header's existing disabled={isAiBusy} keeps working unchanged —
    // it just also covers the ended-session-restart-in-flight window now.
    isAiBusy: isPending || isOtherTabAwaitingReply || isReconnectingAfterEnd,
    sendTyping,
  }
}

export default useChatV2
